import React, { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { TanTable } from './TanTable';
import { useHistoryPanelData } from '../hooks/useHistoryPanelData';
import { formatHistoryDateTime } from '../utils/dateUtils';
import tablemaintService, { TablemaintRow } from '../services/tablemaint.service';
import { retriggerEdiEvent, type EdiRetriggerSource } from '../services/edievents.service';
import { ack997BadgeClass, ack997MatchNote } from '../utils/edi997';
import type { CustomDrawerProps } from './tablemaint/TablemaintPage';

interface EdiEventRecord {
  source: string;
  recno: number;
  sentreceived: string;
  edihandle: string;
  eventtype: string;
  eventlabel: string;
  /** X12 B2A02 transaction set purpose — outbound 204 (`sendorder`) only.
   *  '00' Original | '04' Change | '01' Cancellation. Blank for every other source. */
  purposecode?: string | null;
  eventdate: string | null;
  sentdate: string | null;
  /** Inbound 997 functional acknowledgment for this record, when one came back.
   *  Only the four sources cms.edi_997 maps to (events, archived events, orders,
   *  sent invoices) can carry one; everything else is always null. */
  ackcode?: string | null;
  ackresult?: string | null;
  ackdate?: string | null;
  ackmatchstatus?: string | null;
}

interface EdiEventsResponse {
  count: number;
  records: EdiEventRecord[];
}

interface EdiEventsPanelProps {
  pronumber: number | null;
  /** Defer fetching until the panel is actually shown. */
  active?: boolean;
  /** Bump this number to trigger a refetch from the parent. */
  refreshNonce?: number;
}

const sentReceivedBadgeClass = (v: string): string => {
  if (v === 'sent') return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300';
  if (v === 'received') return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  return 'bg-gray-100 text-label dark:bg-gray-700';
};

// Row-click opens the source's existing tablemaint edit drawer in place. Only the
// three sources that have a gwi360 drawer are clickable (matching legacy, which only
// opened editors for edi_events / edi_orders / edi_inventoryadj here); the drawers are
// lazy-loaded so this heavy tablemaint code stays out of the pro-history bundle until
// a row is actually opened. Each drawer reads a full tablemaint row, so we fetch it via
// tablemaintService.get(table, recno) before opening (EdiOrders self-refetches, the
// other two read the passed row — fetching the full row satisfies all three).
const EdiEventsEditDrawer = lazy(() => import('./tablemaint/EdiEventsEditDrawer'));
const EdiOrdersEditDrawer = lazy(() => import('./tablemaint/EdiOrdersEditDrawer'));
const EdiInventoryAdjEditDrawer = lazy(() => import('./tablemaint/EdiInventoryAdjEditDrawer'));

const SOURCE_DRAWER: Record<string, { table: string; Drawer: React.ComponentType<CustomDrawerProps> }> = {
  edievents: { table: 'edi-events', Drawer: EdiEventsEditDrawer as React.ComponentType<CustomDrawerProps> },
  ediorders: { table: 'edi-orders', Drawer: EdiOrdersEditDrawer as React.ComponentType<CustomDrawerProps> },
  ediinventoryadj: { table: 'edi-inventory-adj', Drawer: EdiInventoryAdjEditDrawer as React.ComponentType<CustomDrawerProps> },
};

const s = (v: unknown): string => (v == null ? '' : String(v));

// X12 B2A02 transaction set purpose, shown only where it means something (the
// outbound 204). '00' is deliberately NOT listed: it is the column default that every
// non-204 event carries, so badging it would put "Original" on every row in the panel.
const PURPOSE_LABELS: Record<string, { label: string; cls: string }> = {
  '04': { label: 'Revision', cls: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300' },
  '01': { label: 'Cancelled', cls: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300' },
};

// Retrigger eligibility mirrors V1 edieventtab (minus its live-env gate): an already-sent
// event/invoice/inventory-adj can be re-sent, and archived events can always be lifted back.
const RETRIGGER_WHEN_SENT = new Set(['edievents', 'edisentinvoices', 'ediinventoryadj']);
const canRetrigger = (r: EdiEventRecord): boolean =>
  r.source === 'edieventsarchive' || (RETRIGGER_WHEN_SENT.has(r.source) && !!r.sentdate);

// A SENT outbound 204 retriggers differently from everything else: the backend inserts
// a new revision row instead of clearing this one's sent date, because re-shipping the
// same row would reach the partner as a second ORIGINAL tender for a load they already
// have. Say so on the control rather than letting "Retrigger" mean two things silently.
const isSendorder = (r: EdiEventRecord): boolean =>
  r.eventtype === 'sendorder' && !!r.sentdate;

/**
 * Per-row Retrigger control with an inline two-tap confirm (send → Confirm) so an
 * accidental click can't resend an EDI event/invoice to a partner. `onRetrigger`
 * throws on failure so the cell can drop back out of its busy state.
 */
const RetriggerCell: React.FC<{
  record: EdiEventRecord;
  onRetrigger: (r: EdiEventRecord) => Promise<void>;
}> = ({ record, onRetrigger }) => {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const stop = (e: React.MouseEvent) => e.stopPropagation();

  if (!canRetrigger(record)) return <span className="text-gray-300 dark:text-gray-600">—</span>;

  if (busy) {
    return (
      <div onClick={stop} className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
    );
  }

  if (confirming) {
    return (
      <div className="flex items-center gap-1" onClick={stop}>
        <button
          type="button"
          title={isSendorder(record) ? 'Confirm — send a revision' : 'Confirm retrigger'}
          onClick={async (e) => {
            stop(e);
            setBusy(true);
            try {
              await onRetrigger(record);
            } catch {
              /* error surfaced by the panel */
            } finally {
              setBusy(false);
              setConfirming(false);
            }
          }}
          className="px-1.5 py-0.5 text-[0.6875rem] font-medium rounded bg-blue-600 text-white hover:bg-blue-700"
        >
          Confirm
        </button>
        <button
          type="button"
          title="Cancel"
          onClick={(e) => { stop(e); setConfirming(false); }}
          className="px-1 py-0.5 text-[0.6875rem] text-muted hover:text-gray-700"
        >
          ✕
        </button>
      </div>
    );
  }

  return (
    <button
      type="button"
      title={isSendorder(record)
        ? 'Retrigger — queues a REVISION (04) of this tender; the partner is told the '
          + 'load changed rather than being sent a second original'
        : 'Retrigger — clears the sent date so the EDI script re-sends this on its next run'}
      onClick={(e) => { stop(e); setConfirming(true); }}
      className="inline-flex items-center justify-center p-1 rounded text-blue-600 hover:text-blue-800 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/30"
    >
      <PaperAirplaneIcon className="w-4 h-4" />
    </button>
  );
};

export const EdiEventsPanel: React.FC<EdiEventsPanelProps> = ({
  pronumber,
  active = true,
  refreshNonce = 0,
}) => {
  // Local refetch counter, layered on top of the parent's refreshNonce, so a
  // retrigger or a drawer save reloads the table without blanking it.
  const [localNonce, setLocalNonce] = useState(0);
  const [drawer, setDrawer] = useState<{ Drawer: React.ComponentType<CustomDrawerProps>; row: TablemaintRow } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const { data, loading, error } = useHistoryPanelData<EdiEventsResponse>(
    pronumber ? `/orders/${pronumber}/edi-events` : null,
    active,
    refreshNonce + localNonce,
    'Failed to fetch EDI events'
  );

  const onRowClick = useCallback(async (r: EdiEventRecord) => {
    const cfg = SOURCE_DRAWER[r.source];
    if (!cfg) return; // sources without a gwi360 drawer aren't openable
    setActionError(null);
    try {
      const row = await tablemaintService.get(cfg.table, r.recno);
      setDrawer({ Drawer: cfg.Drawer, row });
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || e?.message || 'Failed to open record.');
    }
  }, []);

  const onRetrigger = useCallback(async (r: EdiEventRecord) => {
    setActionError(null);
    try {
      await retriggerEdiEvent(r.source as EdiRetriggerSource, r.recno);
      setLocalNonce((n) => n + 1);
    } catch (e: any) {
      setActionError(e?.response?.data?.detail || e?.message || 'Failed to retrigger EDI event.');
      throw e;
    }
  }, []);

  const columns = useMemo<ColumnDef<EdiEventRecord>[]>(
    () => [
      {
        accessorKey: 'sentreceived',
        header: 'Sent/Received',
        size: 110,
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '-';
          return (
            <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${sentReceivedBadgeClass(v)}`}>
              {v}
            </span>
          );
        },
      },
      {
        accessorKey: 'sentdate',
        header: 'Sent/Received Date',
        size: 160,
        cell: (info) => formatHistoryDateTime(info.getValue() as string | null) || '-',
      },
      {
        accessorKey: 'edihandle',
        header: 'EDI Handle',
        size: 140,
        cell: (info) => (info.getValue() as string) || '-',
      },
      {
        accessorKey: 'eventlabel',
        header: 'Event Type',
        size: 240,
        cell: (info) => {
          const record = info.row.original;
          const label = record.eventlabel || record.eventtype;
          if (!label) return '-';
          // Outbound-204 purpose badge. Rendered here rather than as its own column
          // because it is meaningful for exactly one event type — a column would be
          // blank on every other row in the panel. Without it an original, a revision
          // and a cancel are three identical-looking rows, and Retrigger (which
          // creates a revision) has no visible effect.
          const purpose = PURPOSE_LABELS[s(record.purposecode)];
          const badge = purpose ? (
            <span className={`ml-1.5 inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${purpose.cls}`}>
              {purpose.label}
            </span>
          ) : null;
          if (record.eventlabel && record.eventtype && record.eventlabel !== record.eventtype) {
            return (
              <div title={record.eventtype}>
                {record.eventlabel}{badge}
                <div className="text-xs text-muted truncate">
                  {record.eventtype}
                </div>
              </div>
            );
          }
          return <>{label}{badge}</>;
        },
      },
      {
        accessorKey: 'eventdate',
        header: 'Event Date',
        size: 160,
        cell: (info) => formatHistoryDateTime(info.getValue() as string | null) || '-',
      },
      {
        // The 997 the partner sent back for this exact record, joined on
        // (tablename, tablerecno) — most recent wins, because a resent invoice is
        // acknowledged against the same edi_sentinvoices row more than once.
        accessorKey: 'ackcode',
        header: 'Ack',
        size: 130,
        cell: (info) => {
          const r = info.row.original;
          const code = r.ackcode ?? '';
          if (!code) return <span className="text-gray-300 dark:text-gray-600">—</span>;
          const note = ack997MatchNote(r.ackmatchstatus);
          return (
            <span
              title={[r.ackresult || code, note].filter(Boolean).join(' — ')}
              className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${ack997BadgeClass(code)}`}
            >
              {r.ackresult || code}
            </span>
          );
        },
      },
      {
        accessorKey: 'ackdate',
        header: 'Ack Date',
        size: 160,
        cell: (info) => formatHistoryDateTime(info.getValue() as string | null) || '-',
      },
      {
        id: 'retrigger',
        header: 'Retrigger',
        size: 110,
        enableSorting: false,
        enableColumnFilter: false,
        meta: { excludeFromExport: true } as any,
        cell: (info) => <RetriggerCell record={info.row.original} onRetrigger={onRetrigger} />,
      },
    ],
    [onRetrigger]
  );

  return (
    <div className="h-full flex flex-col">
      {(error || actionError) && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 mb-3">
          <p className="text-red-700 dark:text-red-400 text-sm">{actionError || error}</p>
        </div>
      )}
      <div className="flex-1 min-h-[200px]">
        {/* No defaultSorting: the backend pre-sorts (unsent first, then sentdate
            desc) and TanTable preserves input order when sorting state is empty.
            Row click opens the source's edit drawer (edi_events / edi_orders /
            edi_inventoryadj); other sources are non-clickable. */}
        <TanTable
          data={data?.records || []}
          columns={columns}
          loading={loading}
          cardTitle="EDI Events"
          searchPlaceholder="Search EDI events..."
          settingsKey="pro-history-edi-events"
          fillContainer
          enableSearch
          enableReset
          enableShowHideCols
          enableColumnFilters
          enableZebraStriping={false}
          onRowClick={onRowClick}
        />
      </div>

      {drawer && (
        <Suspense fallback={null}>
          <drawer.Drawer
            row={drawer.row}
            onClose={() => setDrawer(null)}
            onSaved={() => { setDrawer(null); setLocalNonce((n) => n + 1); }}
          />
        </Suspense>
      )}
    </div>
  );
};

export default EdiEventsPanel;
